import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from './api';
import { LoginPage } from './pages/LoginPage';
import { SettingsPage } from './pages/SettingsPage';
import { AccountsPage } from './pages/AccountsPage';
import { FilesPage } from './pages/FilesPage';
import { BlacklistPage } from './pages/BlacklistPage';
import { LogsPage } from './pages/LogsPage';
import { SearchPage } from './pages/SearchPage';
import { ProblemContactsPage } from './pages/ProblemContactsPage';
import { LeadsPage } from './pages/LeadsPage';

const MENU = [
  { to: '/settings', label: 'Настройки', permission: 'settings.manage' },
  { to: '/accounts', label: 'Аккаунты', permission: 'accounts.manage' },
  { to: '/files', label: 'Файлы', permission: 'files.manage' },
  { to: '/leads', label: 'Лиды', permission: 'leads.manage' },
  { to: '/blacklist', label: 'Черный список', permission: 'blacklist.manage' },
  { to: '/logs', label: 'Логи', permission: 'logs.view' },
  { to: '/search', label: 'Поиск', permission: 'search.view' },
  { to: '/problem-contacts', label: 'Проблемные контакты', permission: 'problem_contacts.manage' }
];

function hasPermission(user, permission) {
  const permissions = user?.permissions || [];
  return permissions.includes('*') || permissions.includes(permission);
}

function getFirstAllowedPath(user) {
  const firstVisibleMenuItem = MENU.find((item) => hasPermission(user, item.permission));
  return firstVisibleMenuItem?.to || '/';
}

function Layout({ user, onLogout, onThemeChange, appError, children }) {
  const location = useLocation();
  const [projectName, setProjectName] = useState('');
  const visibleMenu = MENU.filter((item) => hasPermission(user, item.permission));

  useEffect(() => {
    let active = true;
    fetch(`${process.env.PUBLIC_URL}/project-config.json`, { cache: 'no-store' })
      .then((response) => { if (!response.ok) throw new Error('Project config unavailable'); return response.json(); })
      .then((config) => {
        if (active && ['Alfa1', 'Alfa2', 'Tochka'].includes(config.projectName)) {
          setProjectName(config.projectName);
          document.title = `Callnote — ${config.projectName}`;
        }
      }).catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <div className={`app theme-${user?.theme || 'orange_black'}`}>
      <header className="topbar">
        <div className="brand brand-with-logo">
          <img
            src={`${process.env.PUBLIC_URL}/favicon-256.png`}
            alt="Callnote"
            className="brand-logo"
          />
          <span>Callnote{projectName ? ` проект ${projectName}` : ''}</span>
        </div>
        <nav className="menu">
          {visibleMenu.map((item) => (
              <Link key={item.to} className={location.pathname.startsWith(item.to) ? 'active' : ''}
                    to={item.to}>{item.label}</Link>
          ))}
          <button type="button" className="link-button" onClick={onLogout}>Выход</button>
        </nav>
        <select value={user?.theme || 'orange_black'} onChange={(e) => onThemeChange(e.target.value)}>
          <option value="orange_black">Orange / Black</option>
          <option value="blue_white">Blue / White</option>
        </select>
       </header>
      {appError ? <div className="page"><section className="card error-banner">{appError}</section></div> : null}
      <main className="page">{children}</main>
    </div>
  );
}

function ForbiddenPage() {
  return <section className="card error-banner">У вас нет доступа к этому разделу</section>;
}

export default function App() {
  const [auth, setAuth] = useState({ loading: true, user: null });
  const [appError, setAppError] = useState('');
  const navigate = useNavigate();


  useEffect(() => {
    function handleUnauthorized(event) {
      localStorage.removeItem('callnote_token');
      setAuth({ loading: false, user: null });
      setAppError(event?.detail?.message || 'Сессия истекла. Войдите снова');
    }

    window.addEventListener('callnote:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('callnote:unauthorized', handleUnauthorized);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('callnote_token');
    if (!token) {
      setAuth({ loading: false, user: null });
      return;
    }
    api('/auth/me')
      .then((data) => setAuth({ loading: false, user: data.user }))
      .catch(() => {
        localStorage.removeItem('callnote_token');
        setAuth({ loading: false, user: null });
      });
  }, []);

  const actions = useMemo(() => ({
    onLogin(result) {
      localStorage.setItem('callnote_token', result.token);
      setAppError('');
      setAuth({ loading: false, user: result.account });
      navigate(getFirstAllowedPath(result.account), { replace: true });
    },
    onLogout() {
      localStorage.removeItem('callnote_token');
      setAppError('');
      setAuth({ loading: false, user: null });
    },
    async onThemeChange(theme) {
      try {
        setAppError('');
        await api('/auth/me/theme', {
          method: 'PUT',
          body: JSON.stringify({ theme })
        });
        setAuth((prev) => ({ ...prev, user: { ...prev.user, theme } }));
      } catch (error) {
        setAppError(error.message || 'Не удалось изменить тему');
      }
    }
  }), [auth.user, navigate]);

  const firstAllowedPath = useMemo(() => getFirstAllowedPath(auth.user), [auth.user]);

  if (auth.loading) return <div className="centered">Загрузка...</div>;
  if (!auth.user) return <LoginPage onLogin={actions.onLogin} initialError={appError} onClearError={() => setAppError('')} />;

  return (
    <Layout user={auth.user} onLogout={actions.onLogout} onThemeChange={actions.onThemeChange} appError={appError}>
      <Routes>
        <Route path="/settings" element={hasPermission(auth.user, 'settings.manage') ? <SettingsPage /> : <ForbiddenPage />} />
        <Route path="/accounts" element={hasPermission(auth.user, 'accounts.manage') ? <AccountsPage /> : <ForbiddenPage />} />
        <Route path="/files" element={hasPermission(auth.user, 'files.manage') ? <FilesPage /> : <ForbiddenPage />} />
        <Route path="/leads" element={hasPermission(auth.user, 'leads.manage') ? <LeadsPage /> : <ForbiddenPage />} />
        <Route path="/blacklist" element={hasPermission(auth.user, 'blacklist.manage') ? <BlacklistPage /> : <ForbiddenPage />} />
        <Route path="/logs" element={hasPermission(auth.user, 'logs.view') ? <LogsPage /> : <ForbiddenPage />} />
        <Route path="/search" element={hasPermission(auth.user, 'search.view') ? <SearchPage /> : <ForbiddenPage />} />
        <Route path="/problem-contacts" element={hasPermission(auth.user, 'problem_contacts.manage') ? <ProblemContactsPage /> : <ForbiddenPage />} />
        <Route path="*" element={<Navigate to={firstAllowedPath || '/settings'} replace />} />
      </Routes>
    </Layout>
  );
}
